import React, { useState } from 'react';
import { TopBar } from '@/components/TopBar';
import { Page } from '@/components/Page';
import { useApp } from '@/context/AppContext';
import { useQuery } from '@tanstack/react-query';
import api from '@/lib/api';
import { money } from '@/lib/helpers';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { TrendingUp, Wallet, Coins, Percent, AlertTriangle, Loader2 } from 'lucide-react';

const PRESETS = {
  all: { label: 'All time', range: () => ({ start: '', end: '' }) },
  month: { label: 'This month', range: () => { const n = new Date(); return { start: `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-01`, end: '' }; } },
  d30: { label: 'Last 30 days', range: () => { const n = new Date(); const s = new Date(n.getTime() - 29 * 864e5); return { start: s.toISOString().slice(0, 10), end: '' }; } },
};

export default function ProfitReport() {
  const { stores } = useApp();
  const [storeScope, setStoreScope] = useState('ALL');
  const [preset, setPreset] = useState('all');
  const { start, end } = PRESETS[preset].range();

  const params = {};
  if (storeScope !== 'ALL') params.store_id = storeScope;
  if (start) params.start = start;
  if (end) params.end = end;

  const { data, isLoading } = useQuery({
    queryKey: ['profit', storeScope, preset],
    queryFn: async () => (await api.get('/reports/profit', { params })).data.data,
  });

  const chart = (data?.daily || []).map((d) => ({ date: d.date.slice(5), profit: d.profit, revenue: d.revenue }));

  return (
    <Page>
      <TopBar title="Profit Report" />
      <div className="mx-auto max-w-5xl px-4 pt-4 lg:px-6">
        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2">
          <Select value={preset} onValueChange={setPreset}>
            <SelectTrigger className="h-11 w-[160px] rounded-xl" data-testid="profit-period"><SelectValue /></SelectTrigger>
            <SelectContent>{Object.entries(PRESETS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
          </Select>
          {stores.length > 1 && (
            <Select value={storeScope} onValueChange={setStoreScope}>
              <SelectTrigger className="h-11 w-[150px] rounded-xl" data-testid="profit-store"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All Stores</SelectItem>
                {stores.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </div>

        {isLoading || !data ? (
          <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : (
          <>
            {/* Summary cards */}
            <div className="mt-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
              <StatCard testid="profit-revenue" icon={Wallet} tone="text-primary" label="Revenue" value={money(data.revenue)} sub={`${data.invoices} invoices`} />
              <StatCard testid="profit-cogs" icon={Coins} tone="text-amber-600" label="Cost of Goods" value={money(data.cogs)} sub={`${data.items_sold} items sold`} />
              <StatCard testid="profit-net" icon={TrendingUp} tone="text-emerald-600" label="Gross Profit" value={money(data.profit)} highlight sub="Revenue − cost" />
              <StatCard testid="profit-margin" icon={Percent} tone="text-emerald-600" label="Margin" value={`${data.margin}%`} sub="of revenue" />
            </div>

            {data.items_missing_cost > 0 && (
              <div className="mt-3 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800" data-testid="profit-missing-note">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{data.items_missing_cost} sold item(s) have no recorded cost (manual/unmatched lines). Profit may be slightly overstated for these.</span>
              </div>
            )}

            {/* Profit trend */}
            <div className="mt-4 rounded-2xl border border-border bg-card p-4">
              <p className="mb-3 text-sm font-semibold">Daily profit</p>
              <div className="h-60">
                {chart.length === 0 ? <p className="py-10 text-center text-sm text-muted-foreground">No sales in this period.</p> : (
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chart} margin={{ left: -8, right: 8, top: 8 }}>
                      <defs>
                        <linearGradient id="profitFill" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="hsl(160 84% 39%)" stopOpacity={0.35} />
                          <stop offset="100%" stopColor="hsl(160 84% 39%)" stopOpacity={0.02} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(215 20% 90%)" vertical={false} />
                      <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="#94a3b8" />
                      <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" />
                      <Tooltip formatter={(v, n) => [money(v), n === 'profit' ? 'Profit' : 'Revenue']} />
                      <Area type="monotone" dataKey="profit" stroke="hsl(160 84% 39%)" strokeWidth={2.5} fill="url(#profitFill)" />
                    </AreaChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>

            {/* Per store */}
            {data.by_store.length > 0 && (
              <div className="mt-4 overflow-hidden rounded-2xl border border-border bg-card">
                <Table data-testid="profit-store-table">
                  <TableHeader>
                    <TableRow className="bg-muted/50 hover:bg-muted/50">
                      <TableHead>Store</TableHead>
                      <TableHead className="text-right">Revenue</TableHead>
                      <TableHead className="text-right">Cost</TableHead>
                      <TableHead className="text-right">Profit</TableHead>
                      <TableHead className="text-right">Margin</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.by_store.map((s) => (
                      <TableRow key={s.store_id} data-testid={`profit-store-${s.store_id}`}>
                        <TableCell className="font-medium">{s.store_name}</TableCell>
                        <TableCell className="text-right font-mono tabular-nums">{money(s.revenue)}</TableCell>
                        <TableCell className="text-right font-mono tabular-nums text-muted-foreground">{money(s.cogs)}</TableCell>
                        <TableCell className="text-right font-mono font-semibold tabular-nums text-emerald-600">{money(s.profit)}</TableCell>
                        <TableCell className="text-right font-mono tabular-nums">{s.margin}%</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </>
        )}
      </div>
    </Page>
  );
}

const StatCard = ({ icon: Icon, label, value, sub, tone, highlight, testid }) => (
  <div data-testid={testid} className={`rounded-2xl border p-4 ${highlight ? 'border-emerald-200 bg-emerald-50/50' : 'border-border bg-card'}`}>
    <div className={`flex items-center gap-2 ${tone}`}>
      <Icon className="h-4 w-4" />
      <span className="text-xs font-medium">{label}</span>
    </div>
    <div className="mt-2 font-mono text-xl font-semibold tabular-nums lg:text-2xl">{value}</div>
    {sub && <p className="mt-0.5 text-xs text-muted-foreground">{sub}</p>}
  </div>
);
